// web/src/shell/AppShell.tsx
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Direction } from "radix-ui";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";
import { UpstreamHealthBanner } from "./UpstreamHealthBanner";
import { useCurrentUser } from "./useCurrentUser";
import { useMatchesRealtime } from "@/monitors/realtime";
import { BellButton } from "@/monitors/components/BellButton";
import { PendingContextProvider } from "@/copilot/PendingContextProvider";
import { CopilotProvider } from "@/copilot/CopilotProvider";
import { AskTapelensButton } from "@/copilot/AskTapelensButton";
import { useRouteContextReadable } from "@/copilot/useRouteContextReadable";
import { useFilterNotesPageAction } from "@/copilot/useFilterNotesPageAction";
import { useSwitchMaterialAction } from "@/copilot/actions/switchMaterialAction";
import { useRefreshAnalysisAction } from "@/copilot/actions/refreshAnalysisAction";
import { NotesUIControllerProvider } from "@/notes/NotesUIController";
import { DashboardControllerProvider } from "@/trading/DashboardController";
import { useLanguage } from "@/i18n/useLanguage";

/**
 * Returns the i18n key (under namespace "common") for the page title that
 * should render in the top bar for the given route. Centralizing the
 * pathname → key mapping keeps the resolution side-effect free and lets the
 * consumer call `t()` once.
 */
function titleKeyForPath(pathname: string): string {
  if (pathname.startsWith("/monitors")) return "nav.monitors";
  if (pathname.startsWith("/dashboard")) return "nav.dashboard";
  if (pathname.startsWith("/calendar")) return "nav.calendar";
  if (pathname.startsWith("/clusters")) return "nav.clusters";
  if (pathname.startsWith("/notes")) return "nav.notes";
  if (pathname.startsWith("/profile")) return "nav.profile";
  if (pathname.startsWith("/account")) return "nav.account";
  if (pathname.startsWith("/preferences")) return "nav.preferences";
  return "nav.appName";
}

/**
 * Inner shell rendered *below* CopilotProvider so the CopilotKit-aware
 * hooks (route_context, filter_notes_page) can run. Splitting this out
 * keeps the outer component focused on provider composition.
 */
function AppShellInner() {
  const { pathname } = useLocation();
  const { t } = useTranslation("common");
  // Slice 2: publish current route + register the filter_notes_page action
  // here. These hooks call CopilotKit's useCopilotReadable /
  // useCopilotAction, which require a <CopilotKit> ancestor — hence they
  // sit inside <CopilotProvider>.
  //
  // Slice 3: register switch_material + refresh_analysis here too.
  // switch_material reads from DashboardController and uses
  // react-router's useNavigate; refresh_analysis only needs the API
  // client. Both belong above the route outlet so they're reachable
  // from any authenticated route.
  useRouteContextReadable();
  useFilterNotesPageAction();
  useSwitchMaterialAction();
  useRefreshAnalysisAction();
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      <UpstreamHealthBanner />
      <TopBar title={t(titleKeyForPath(pathname))}>
        <AskTapelensButton />
        <BellButton />
      </TopBar>
      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        <Sidebar />
        <main style={{ flex: 1, overflow: "auto", background: "var(--bg)" }}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}

export function AppShell() {
  const { data: me } = useCurrentUser();
  const { rtl } = useLanguage();
  useMatchesRealtime(me?.user_id);
  return (
    // PendingContextProvider mounts at the shell level so sparkle clicks on
    // any authenticated route (dashboard / cluster detail / etc.) can hand a
    // target into the AskTapelens chat sheet. The context is route-scoped
    // (only authenticated routes see it) but shell-wide above the routes.
    //
    // NotesUIControllerProvider lifts the notes slide-over controls to the
    // shell level so the AskTapelens chat's `open_notes` CopilotKit action
    // can drive the same UI from outside TradingView.
    //
    // CopilotProvider lives here (Slice 2) so the Ask Tapelens chat is
    // reachable from every authenticated route — /notes, /calendar,
    // /monitors, etc. — not just /dashboard.
    //
    // DashboardControllerProvider lives here (Slice 3) so CopilotKit
    // actions like `switch_material` can mutate the dashboard pane state
    // from any route. The provider preserves left/right material id
    // persistence via the same localStorage keys the previous per-Pane
    // state used.
    //
    // L5 quality fix: FocusedMaterialProvider was deleted — the
    // dashboard_focus readable on TradingView already publishes
    // focusedSide + leftMaterial + rightMaterial, so the separate
    // focused-material context had no remaining consumers.
    //
    // i18n Slice 1 / F8: Direction.Provider (re-exported from radix-ui as
    // Direction.Provider, alias for @radix-ui/react-direction) tells every
    // Radix primitive in the tree (Tooltip, Popover, Sheet, Dropdown,
    // etc.) which side its arrows / submenus / focus order should flow.
    // Without this, the html[dir="rtl"] attribute alone leaves Radix
    // primitives in LTR mode and they overflow / clip on the wrong side.
    <CopilotProvider>
      <Direction.Provider dir={rtl ? "rtl" : "ltr"}>
        <PendingContextProvider>
          <NotesUIControllerProvider>
            <DashboardControllerProvider>
              <AppShellInner />
            </DashboardControllerProvider>
          </NotesUIControllerProvider>
        </PendingContextProvider>
      </Direction.Provider>
    </CopilotProvider>
  );
}
